/**
 * Brand select field (Epic 40) — used in the Edit Client form to assign the
 * client's owning brand. Pick an existing brand, clear it (No brand), or create a
 * new one inline (which selects it). It only sets the FORM value; the parent form
 * persists the change on save via the brand assign/unassign endpoints. Read-only
 * display of the current brand lives in the Client Info panel.
 */

import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import { Buildings, CaretDown, Plus, Check } from "@phosphor-icons/react";
import { useBrands } from "../../hooks/useBrands";
import type { ToastType } from "../../hooks/useToast";
import { BrandFormDialog } from "../brands/BrandFormDialog";

export function BrandSelectField({
  value,
  onChange,
  showToast,
  disabled,
}: {
  /** The selected brand id, or "" for none. */
  value: string;
  onChange: (brandId: string) => void;
  showToast: (type: ToastType, message: string) => void;
  disabled?: boolean;
}) {
  const { data: brands } = useBrands();
  const [createOpen, setCreateOpen] = useState(false);
  const selected = brands?.find((b) => b.id === value) ?? null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Match the IndustryPicker trigger exactly (h-11 / rounded-lg / 0.5px
              border-input / text-sm) so the field looks identical to the others. */}
          <button
            type="button"
            disabled={disabled}
            data-testid="edit-brand-trigger"
            className="flex h-11 w-full items-center justify-between rounded-lg border-[0.5px] border-input bg-background px-3 py-2 text-sm shadow-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={`flex items-center gap-2 min-w-0 ${selected ? "text-foreground" : "text-muted-foreground"}`}>
              {selected ? (
                <>
                  <Buildings size={16} className="shrink-0 text-foreground" />
                  <span className="truncate">{selected.name}</span>
                </>
              ) : (
                "No brand"
              )}
            </span>
            <CaretDown size={16} className="shrink-0 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="rounded-xl p-2 shadow-md max-h-[280px] overflow-y-auto w-[var(--radix-dropdown-menu-trigger-width)] min-w-56"
          style={{ border: "0.5px solid var(--hairline)" }}
        >
          <DropdownMenuItem
            className="min-h-10 text-[13px] cursor-pointer rounded-lg"
            onClick={() => onChange("")}
            data-testid="edit-brand-none"
          >
            <span className="w-4 mr-1.5 flex justify-center shrink-0">
              {!value && <Check size={13} weight="bold" />}
            </span>
            No brand
          </DropdownMenuItem>
          {(brands ?? []).map((b) => (
            <DropdownMenuItem
              key={b.id}
              className="min-h-10 text-[13px] cursor-pointer rounded-lg"
              onClick={() => onChange(b.id)}
            >
              <span className="w-4 mr-1.5 flex justify-center shrink-0">
                {value === b.id && <Check size={13} weight="bold" />}
              </span>
              <Buildings size={14} className="mr-1.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{b.name}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="min-h-10 text-[13px] cursor-pointer rounded-lg"
            onClick={() => setCreateOpen(true)}
            data-testid="edit-brand-create"
          >
            <Plus size={14} className="mr-1.5 shrink-0" />
            Create new brand
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create a new brand, then select it for this client. */}
      <BrandFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        showToast={showToast}
        onCreated={(brand) => onChange(brand.id)}
      />
    </>
  );
}
